const FC_DESKTOP_MQ = '(min-width: 990px)';
const FC_DRAG_THRESHOLD = 4;

class FeaturedCollectionsScroll {
  constructor(section) {
    this.section = section;
    this.layout = section.querySelector('.featured-collections__layout');
    this.intro = section.querySelector('.featured-collections__intro');
    this.scroller = section.querySelector('[data-fc-scroll]');
    this.progress = section.querySelector('[data-fc-progress]');
    this.progressFill = this.progress?.querySelector('.featured-collections__progress-fill');
    this.nav = section.querySelector('[data-fc-nav]');
    this.prevBtn = section.querySelector('[data-fc-nav-prev]');
    this.nextBtn = section.querySelector('[data-fc-nav-next]');
    this.desktopScrollEnabled = section.dataset.fcDesktopScroll === 'true';
    this.mobileScrollEnabled = section.dataset.fcMobileScroll === 'true';
    this.showProgress = section.dataset.fcShowProgress === 'true';

    if (!this.scroller || !this.layout) return;

    this.desktopMq = window.matchMedia(FC_DESKTOP_MQ);
    this.schedulePaddingFrame = null;
    this.isDragging = false;
    this.pointerId = null;
    this.startX = 0;
    this.scrollStart = 0;
    this.hasDragged = false;
    this.suppressClickUntil = 0;
    this.wheelTarget = null;
    this.wheelFrame = null;
    this.docListenersBound = false;

    this.onWheel = this.onWheel.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.animateWheel = this.animateWheel.bind(this);
    this.updateScrollPadding = this.updateScrollPadding.bind(this);
    this.scheduleScrollPaddingUpdate = this.scheduleScrollPaddingUpdate.bind(this);
    this.onDesktopMqChange = this.onDesktopMqChange.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onNavKeyDown = this.onNavKeyDown.bind(this);

    this.scroller.addEventListener('wheel', this.onWheel, { passive: false });
    this.scroller.addEventListener('pointerdown', this.onPointerDown);
    this.scroller.addEventListener('click', this.onClickCapture, true);
    this.scroller.addEventListener('dragstart', this.onDragStart);
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNextClick);
    if (this.nav) this.nav.addEventListener('keydown', this.onNavKeyDown);

    this.resizeObserver = new ResizeObserver(() => this.scheduleScrollPaddingUpdate());
    this.resizeObserver.observe(this.layout);
    if (this.intro) this.resizeObserver.observe(this.intro);
    this.resizeObserver.observe(this.scroller);

    this.desktopMq.addEventListener('change', this.onDesktopMqChange);
    window.addEventListener('resize', this.scheduleScrollPaddingUpdate);
    window.addEventListener('load', this.scheduleScrollPaddingUpdate);
    if (this.intro) {
      this.intro.addEventListener('animationend', this.scheduleScrollPaddingUpdate);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.scheduleScrollPaddingUpdate());
    }

    this.scheduleScrollPaddingUpdate();
    this.scheduleScrollPaddingUpdate({ delayFrames: 3 });
    this.updateProgress();
    this.updateNavState();
  }

  get isDesktopMode() {
    return this.desktopMq.matches && this.desktopScrollEnabled;
  }

  get isMobileScrollMode() {
    return !this.desktopMq.matches && this.mobileScrollEnabled;
  }

  get scrollInteractionsEnabled() {
    return this.isDesktopMode || this.isMobileScrollMode;
  }

  get introOverlapPx() {
    return 0;
  }

  getScrollPadding() {
    return 0;
  }

  onDesktopMqChange() {
    this.scheduleScrollPaddingUpdate();
    this.updateProgress();
    this.updateNavState();
  }

  onScroll() {
    this.updateProgress();
    this.updateNavState();
  }

  scheduleScrollPaddingUpdate(options = {}) {
    const { delayFrames = 1 } = options;

    if (this.schedulePaddingFrame !== null) {
      cancelAnimationFrame(this.schedulePaddingFrame);
      this.schedulePaddingFrame = null;
    }

    let remaining = delayFrames;

    const run = () => {
      remaining -= 1;
      if (remaining > 0) {
        this.schedulePaddingFrame = requestAnimationFrame(run);
        return;
      }

      this.schedulePaddingFrame = null;
      this.clearScrollPadding();
      this.updateProgress();
      this.updateNavState();
    };

    this.schedulePaddingFrame = requestAnimationFrame(run);
  }

  clearScrollPadding() {
    this.scroller.style.removeProperty('padding-left');
    this.scroller.style.removeProperty('scroll-padding-left');
    this.scroller.removeAttribute('data-fc-scroll-padding');
    this.scroller.removeAttribute('data-fc-intro-width');
    this.scroller.removeAttribute('data-fc-scroller-offset');
  }

  updateScrollPadding() {
    this.clearScrollPadding();
  }

  getSlides() {
    return Array.from(this.scroller.querySelectorAll('.featured-collections__slide'));
  }

  getScrollPaddingLeft() {
    return parseFloat(getComputedStyle(this.scroller).scrollPaddingLeft) || 0;
  }

  getScrollProgressRatio() {
    const maxScroll = this.maxScroll;
    if (maxScroll <= 0) return 0;

    const slides = this.getSlides();
    if (slides.length > 1) {
      const scrollPaddingLeft = this.getScrollPaddingLeft();
      const lastSnapScroll = Math.max(0, slides[slides.length - 1].offsetLeft - scrollPaddingLeft);
      const effectiveMax = Math.min(lastSnapScroll, maxScroll);

      if (effectiveMax > 0) {
        return Math.min(this.scroller.scrollLeft / effectiveMax, 1);
      }
    }

    return Math.min(this.scroller.scrollLeft / maxScroll, 1);
  }

  updateProgress() {
    if (!this.showProgress || !this.progressFill || !this.isMobileScrollMode) return;

    const maxScroll = this.maxScroll;
    if (maxScroll <= 0) {
      this.progressFill.style.width = '100%';
      this.progressFill.style.left = '0';
      this.progressFill.style.transform = 'translateX(0)';
      return;
    }

    const visibleRatio = Math.min(this.scroller.clientWidth / this.scroller.scrollWidth, 1);
    const widthPct = Math.max(visibleRatio * 100, 8);
    const ratio = this.getScrollProgressRatio();
    const leftPct = ratio * Math.max(100 - widthPct, 0);

    this.progressFill.style.width = `${widthPct}%`;
    this.progressFill.style.left = `${leftPct}%`;
    this.progressFill.style.transform = 'translateX(0)';
  }

  updateNavState() {
    if (!this.prevBtn && !this.nextBtn) return;

    const maxScroll = this.maxScroll;
    const atStart = this.scroller.scrollLeft <= 2;
    const atEnd = this.scroller.scrollLeft >= maxScroll - 2;
    const disabled = !this.scrollInteractionsEnabled || maxScroll <= 0;

    if (this.prevBtn) {
      const prevDisabled = disabled || atStart;
      this.prevBtn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      this.prevBtn.disabled = prevDisabled;
    }

    if (this.nextBtn) {
      const nextDisabled = disabled || atEnd;
      this.nextBtn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      this.nextBtn.disabled = nextDisabled;
    }
  }

  scrollBySlide(direction) {
    if (!this.scrollInteractionsEnabled || this.maxScroll <= 0) return;

    const slides = this.getSlides();
    if (!slides.length) return;

    const current = this.scroller.scrollLeft;
    const scrollPaddingLeft = this.getScrollPaddingLeft();
    let targetScroll = current;

    if (direction > 0) {
      const next = slides.find((slide) => slide.offsetLeft - scrollPaddingLeft > current + 2);
      targetScroll = next ? next.offsetLeft - scrollPaddingLeft : this.maxScroll;
    } else {
      const prevSlides = slides.filter((slide) => slide.offsetLeft - scrollPaddingLeft < current - 2);
      const prev = prevSlides[prevSlides.length - 1];
      targetScroll = prev ? prev.offsetLeft - scrollPaddingLeft : 0;
    }

    this.wheelTarget = null;
    if (this.wheelFrame !== null) {
      cancelAnimationFrame(this.wheelFrame);
      this.wheelFrame = null;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scroller.scrollTo({
      left: this.clampScroll(targetScroll),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  onPrevClick(event) {
    event.preventDefault();
    if (this.prevBtn?.disabled) return;
    this.scrollBySlide(-1);
  }

  onNextClick(event) {
    event.preventDefault();
    if (this.nextBtn?.disabled) return;
    this.scrollBySlide(1);
  }

  onNavKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.scrollBySlide(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.scrollBySlide(1);
    }
  }

  bindDocListeners() {
    if (this.docListenersBound) return;
    document.addEventListener('pointermove', this.onPointerMove, { passive: false });
    document.addEventListener('pointerup', this.onPointerEnd);
    document.addEventListener('pointercancel', this.onPointerEnd);
    this.docListenersBound = true;
  }

  unbindDocListeners() {
    if (!this.docListenersBound) return;
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerEnd);
    document.removeEventListener('pointercancel', this.onPointerEnd);
    this.docListenersBound = false;
  }

  destroy() {
    if (this.schedulePaddingFrame !== null) {
      cancelAnimationFrame(this.schedulePaddingFrame);
      this.schedulePaddingFrame = null;
    }

    if (this.wheelFrame !== null) {
      cancelAnimationFrame(this.wheelFrame);
      this.wheelFrame = null;
    }

    this.unbindDocListeners();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.desktopMq.removeEventListener('change', this.onDesktopMqChange);
    window.removeEventListener('resize', this.scheduleScrollPaddingUpdate);
    window.removeEventListener('load', this.scheduleScrollPaddingUpdate);
    if (this.intro) {
      this.intro.removeEventListener('animationend', this.scheduleScrollPaddingUpdate);
    }
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNextClick);
    if (this.nav) this.nav.removeEventListener('keydown', this.onNavKeyDown);
    this.clearScrollPadding();
  }

  get maxScroll() {
    return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
  }

  clampScroll(value) {
    return Math.min(this.maxScroll, Math.max(0, value));
  }

  onWheel(event) {
    if (!this.isDesktopMode || this.maxScroll <= 0) return;

    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const delta = event.deltaX;

    if (delta === 0) return;

    const next =
      (this.wheelTarget ?? this.scroller.scrollLeft) + delta * 0.85;

    if (next <= 0 && delta < 0) return;
    if (next >= this.maxScroll && delta > 0) return;

    event.preventDefault();

    this.wheelTarget = this.clampScroll(next);

    if (this.wheelFrame === null) {
      this.wheelFrame = requestAnimationFrame(this.animateWheel);
    }
  }

  animateWheel() {
    const current = this.scroller.scrollLeft;
    const target = this.wheelTarget ?? current;
    const distance = target - current;

    if (Math.abs(distance) < 0.5) {
      this.scroller.scrollLeft = target;
      this.wheelTarget = null;
      this.wheelFrame = null;
      return;
    }

    this.scroller.scrollLeft = current + distance * 0.22;
    this.wheelFrame = requestAnimationFrame(this.animateWheel);
  }

  onDragStart(event) {
    if (!this.scrollInteractionsEnabled || this.isMobileScrollMode) return;
    event.preventDefault();
  }

  onPointerDown(event) {
    if (!this.scrollInteractionsEnabled) return;
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (this.maxScroll <= 0) return;
    if (event.target.closest('[data-fc-nav]')) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.scrollStart = this.scroller.scrollLeft;
    this.wheelTarget = null;

    if (this.wheelFrame !== null) {
      cancelAnimationFrame(this.wheelFrame);
      this.wheelFrame = null;
    }

    this.bindDocListeners();
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    const walk = event.clientX - this.startX;

    if (!this.hasDragged && Math.abs(walk) > FC_DRAG_THRESHOLD) {
      this.hasDragged = true;
      this.scroller.classList.add('is-dragging');
    }

    if (!this.hasDragged) return;

    event.preventDefault();
    this.scroller.scrollLeft = this.clampScroll(this.scrollStart - walk);
  }

  onPointerEnd(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    this.isDragging = false;
    this.pointerId = null;
    this.scroller.classList.remove('is-dragging');
    this.unbindDocListeners();

    if (this.hasDragged) {
      this.suppressClickUntil = Date.now() + 400;
    }

    this.updateProgress();
    this.updateNavState();
  }

  onClickCapture(event) {
    if (Date.now() < this.suppressClickUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}

function initFeaturedCollectionsScroll(root = document) {
  const sections = root.matches?.('[data-featured-collections-scroll]')
    ? [root]
    : Array.from(root.querySelectorAll('[data-featured-collections-scroll]'));

  sections.forEach((section) => {
    if (section._fcScrollInstance) {
      section._fcScrollInstance.destroy();
    }

    section._fcScrollInstance = new FeaturedCollectionsScroll(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturedCollectionsScroll);
} else {
  initFeaturedCollectionsScroll();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  const section =
    root.matches?.('[data-featured-collections-scroll]')
      ? root
      : root.querySelector?.('[data-featured-collections-scroll]');

  if (section) {
    initFeaturedCollectionsScroll(section);
  }
});
